import { useEffect, useState } from 'react';
import { useUserStore, type PmoUserRecord } from '../store/useUserStore';
import { useAuthStore, type PmoRole } from '../store/useAuthStore';
import UserTable from '../components/admin/UserTable';
import UserRoleEditor from '../components/admin/UserRoleEditor';
import AddUserDialog from '../components/admin/AddUserDialog';
import AuthGuard from '../components/auth/AuthGuard';
import toast from 'react-hot-toast';
import { UserPlus, ShieldCheck } from 'lucide-react';
import RefreshButton from '../components/common/RefreshButton';

export default function AdminUsersPage() {
  const { users, loading, fetchUsers, updateUser, updateRole, assignProjects, deactivateUser, createUser } = useUserStore();
  const { user: currentUser } = useAuthStore();
  const [editingUser, setEditingUser] = useState<PmoUserRecord | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEdit = (user: PmoUserRecord) => {
    setEditingUser(user);
  };

  const handleDeactivate = async (user: PmoUserRecord) => {
    if (!confirm(`Deactivate ${user.name}? They will lose access to the PMO.`)) return;
    try {
      await deactivateUser(user._id);
      toast.success(`${user.name} has been deactivated`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to deactivate user');
    }
  };

  const handleSave = async (userId: string, updates: { role?: PmoRole; assignedProjects?: string[]; additionalEmails?: string[]; gitIdentities?: string[] }) => {
    if (updates.role) {
      await updateRole(userId, updates.role);
    }
    if (updates.assignedProjects !== undefined) {
      await assignProjects(userId, updates.assignedProjects);
    }
    // If other fields were changed
    const otherUpdates = { ...updates };
    delete otherUpdates.role;
    delete otherUpdates.assignedProjects;
    if (Object.keys(otherUpdates).length > 0) {
      await updateUser(userId, otherUpdates);
    }
    toast.success('User updated successfully');
    await fetchUsers(); // Refresh
  };

  // Summary stats
  const activeUsers = users.filter((u) => u.active);

  const handleAddUser = async (data: { email: string; name: string; role: string }) => {
    await createUser(data);
    toast.success(`${data.name} has been added and a welcome email was sent`);
  };
  const roleCounts = activeUsers.reduce(
    (acc, u) => {
      acc[u.role] = (acc[u.role] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <AuthGuard roles={['super_admin']}>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
              <ShieldCheck size={20} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">User Management</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage user roles and project access
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <RefreshButton
              onRefresh={async () => { await fetchUsers(); }}
              tooltip="Refresh users"
            />
            <button
              onClick={() => setShowAddDialog(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-accent hover:bg-accent/90 transition-colors shadow-sm"
            >
              <UserPlus size={16} />
              Add User
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Users</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{users.length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Active</p>
            <p className="text-2xl font-bold text-green-600">{activeUsers.length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Super Admins</p>
            <p className="text-2xl font-bold text-red-600">{roleCounts.super_admin || 0}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Project Admins</p>
            <p className="text-2xl font-bold text-blue-600">{roleCounts.project_admin || 0}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Resources</p>
            <p className="text-2xl font-bold text-amber-600">{roleCounts.project_resource || 0}</p>
          </div>
        </div>

        {/* User Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <UserTable
            users={users}
            onEdit={handleEdit}
            onDeactivate={handleDeactivate}
            currentUserEmail={currentUser?.email || ''}
          />
        )}

        {/* Role Editor Modal */}
        {editingUser && (
          <UserRoleEditor
            user={editingUser}
            onSave={handleSave}
            onClose={() => setEditingUser(null)}
            isSelf={editingUser.email === currentUser?.email}
          />
        )}

        {/* Add User Dialog */}
        <AddUserDialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          onSubmit={handleAddUser}
        />
      </div>
    </AuthGuard>
  );
}
